import { useState, useRef, useMemo, useContext, useEffect } from "react";
import { useDispatch } from "react-redux";
import { userSlice } from "@/redux/User";
import {
  DeleteIconBig,
  LinkIcon,
  RefreshIcon,
  PrimaryClrLinkIcon,
  SearchLinkIcon,
} from "@/assets/AiBotAssets";
import { BotContext } from "../store";
import type { Url } from "../types";
import { handleSearch } from "@/utils/handleSearch";
import ThreeDot from "@/components/loaders/ThreeDot";
import Pagination from "../Pagination";
import { axiosPost } from "@/services/axiosService";
import CircleLoader from "@/components/loaders/CircleLoader";
import { AnimatePresence, motion } from "framer-motion";
import { deleteCookies, getCookie } from "@/services/cookieService";
import { NEW_USER_INSTRUCTION } from "../AiBotData";
import { useSelector } from "react-redux";
import { State } from "@/redux/Types";
import {
  buildChatConfigFromBranding,
  getBrandingContent,
} from "@/utils/brandingChatConfig";

interface UrlWithId extends Url {
  id: string;
  status?: string;
}

const URL_PER_PAGE = 100;
const PAGE_PER_WINDOW = 5;

function getUrlKey(value: unknown): string {
  if (typeof value !== "string") return "";

  const trimmedValue = value.trim();
  if (!trimmedValue) return "";

  try {
    const parsedUrl = new URL(trimmedValue);
    parsedUrl.hash = "";

    return `${parsedUrl.origin}${
      parsedUrl.pathname === "/" ? "" : parsedUrl.pathname
    }${parsedUrl.search}`;
  } catch {
    return trimmedValue;
  }
}

function matchesUrlSearch(searchInput: string, value: string): boolean {
  const trimmedSearchInput = searchInput.trim();
  if (!trimmedSearchInput) return true;

  return handleSearch(trimmedSearchInput, value);
}

type FetchUrlModalProps = {
  characterUsed: number;
  characterLimit: number;
  fetchMoreUrl: () => void | Promise<void>;
  fetchExtraUrl: (...args: any[]) => void | Promise<void>;
  refreshUrl: () => void | Promise<void>;
  extraUrl: string;
  setExtraUrl: (value: string) => void;
  refreshLoader: boolean;
  fetchMoreLoader: boolean;
  extraLoader: boolean;
  fetchRemains: boolean;
  allTrained: boolean;
  crawlLimit?: number;
  onClose: () => void;
  fetchModal?: boolean;
  onTrain?: (...args: any[]) => void;
  onTrainComplete?: (urlData: UrlWithId[]) => void;
  onDataChange?: (...args: any[]) => void;
  fromOnboarding?: boolean;
};

function FetchUrlModal({
  characterUsed,
  characterLimit,
  fetchMoreUrl,
  fetchExtraUrl,
  refreshUrl,
  extraUrl,
  setExtraUrl,
  refreshLoader,
  fetchMoreLoader,
  extraLoader,
  fetchRemains,
  allTrained,
  crawlLimit,
  onClose,
  fetchModal,
  onTrain,
  onTrainComplete,
  onDataChange,
  fromOnboarding,
}: FetchUrlModalProps) {
  const dispatch = useDispatch();
  const [store, storeDispatch] = useContext(BotContext);
  const [searchInput, setSearchInput] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [checkedSet, setCheckedSet] = useState<Set<string>>(new Set());
  const characterRemaining = characterLimit - characterUsed;
  const [isAccordian, setIsAccordian] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const lastTrainTriggeredAtRef = useRef<number>(0);
  const fromOutgrow = useSelector((state: State) => state.user.fromOutgrow);
  const existingUrlKeys = useMemo(() => {
    const existingUrls = store.botData?.aiSource?.urls;
    if (!Array.isArray(existingUrls)) return new Set<string>();

    return new Set<string>(
      existingUrls
        .map((data: any) => getUrlKey(data?.url ?? data?.link))
        .filter(Boolean),
    );
  }, [store.botData?.aiSource?.urls]);

  useEffect(() => {
    if (/iPad|iPhone/.test(navigator.userAgent)) {
      const resSelectedWrapper = document.querySelector(
        ".delete-links-th",
      ) as HTMLElement;
      if (resSelectedWrapper) {
        resSelectedWrapper.style.right = "15px";
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setIsInputVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const {
    crawlData,
    selectedData,
    extraData,
    crawlLength,
    selectedLength,
    extraLength,
    crawlCharacterCount,
    selectedCharacterCount,
    allChecked,
  } = useMemo(() => {
    const crawlData: Array<UrlWithId> = [];
    const selectedData: Array<UrlWithId> = [];
    const extraData: Array<UrlWithId> = [];

    let crawlLength = 0;
    let selectedLength = 0;
    let extraLength = 0;

    let crawlCharacterCount = 0;
    let selectedCharacterCount = 0;
    let allChecked = true;
    let selectableLength = 0;
    const seenCrawlUrlKeys = new Set<string>();
    const selectedUrlKeys = new Set<string>();

    store.crawlData.forEach((value, key) => {
      const urlKey = getUrlKey(value.url);
      if (urlKey && seenCrawlUrlKeys.has(urlKey)) return;
      if (urlKey) seenCrawlUrlKeys.add(urlKey);

      let searchCheck = true;
      if (searchInput) {
        searchCheck = matchesUrlSearch(searchInput, value.url);
      }

      const alreadyAdded = existingUrlKeys.has(urlKey);
      const isSelected = value.selected && !alreadyAdded;

      if (searchCheck) {
        crawlData.push({
          id: key,
          ...value,
          selected: isSelected || alreadyAdded,
        });
      }
      crawlCharacterCount += value.characterCount;
      crawlLength++;

      if (!alreadyAdded) {
        selectableLength++;
      }

      if (isSelected) {
        if (searchCheck)
          selectedData.push({ id: key, status: "In Progress", ...value });
        selectedCharacterCount += value.characterCount;
        selectedLength++;
        if (urlKey) selectedUrlKeys.add(urlKey);
      } else if (!alreadyAdded) {
        allChecked = false;
      }
    });

    store.extraData.forEach((value, key) => {
      if (!value.selected) return;

      const urlKey = getUrlKey(value.url);
      if (
        existingUrlKeys.has(urlKey) ||
        (urlKey && selectedUrlKeys.has(urlKey))
      ) {
        return;
      }

      let searchCheck = true;
      if (searchInput) {
        searchCheck = matchesUrlSearch(searchInput, value.url);
      }

      if (searchCheck) extraData.push({ id: key, ...value });
      selectedCharacterCount += value.characterCount;
      extraLength++;
      if (urlKey) selectedUrlKeys.add(urlKey);
    });

    allChecked = selectableLength > 0 && allChecked;
    setCheckedSet(new Set());

    return {
      crawlData,
      selectedData,
      extraData,
      crawlLength,
      selectedLength,
      extraLength,
      crawlCharacterCount,
      selectedCharacterCount,
      allChecked,
    };
  }, [existingUrlKeys, searchInput, store.crawlData, store.extraData]);

  const paginatedCrawlData = useMemo(
    () => crawlData.slice(page * URL_PER_PAGE, (page + 1) * URL_PER_PAGE),
    [crawlData, page],
  );

  function sendInstructionMsg() {
    const userId = store.botData.userId ?? "";
    const isOnboarding = getCookie(
      `company-onboarding-triggerMilaBot:${userId}`,
    );

    if (isOnboarding === "true") {
      window.ChatbotBuilder?.sendMessage(NEW_USER_INSTRUCTION, "assistant");
      deleteCookies(`company-onboarding-triggerMilaBot:${userId}`);
    }
  }

  async function handleTrain() {
    const seenUrlKeys = new Set(existingUrlKeys);
    const urlData = selectedData.filter((data) => {
      const urlKey = getUrlKey(data.url);
      if (!urlKey || seenUrlKeys.has(urlKey)) return false;

      seenUrlKeys.add(urlKey);
      return true;
    });

    if (!urlData.length) return;

    const now = Date.now();
    if (now - lastTrainTriggeredAtRef.current < 5000) {
      return;
    }
    lastTrainTriggeredAtRef.current = now;
    setIsTraining(true);
    try {
      storeDispatch({
        type: "add-aiSource",
        dataKey: "urls",
        dataSet: urlData,
      });

      if (onTrainComplete) {
        onTrainComplete(urlData);
        setIsInputVisible(false);
      }

      const response = await axiosPost("/train/url", {
        urls: urlData.map((url) => url.url),
        botCustomId: store.botData.botCustomId,
        botCreatedAt: store.botData.createdAt,
        fromOutgrow,
      });

      // let brandingContent = getBrandingContent(response.data);

      // if (!brandingContent && urlData.length > 0) {
      //   try {
      //     const brandingRes = await axiosPost("/ai/fetchBranding", {
      //       url: urlData[0].url,
      //       botCustomId: store.botData.botCustomId,
      //     });
      //     brandingContent = getBrandingContent(brandingRes.data);
      //   } catch (e) {
      //     console.error("Failed to fetch branding", e);
      //   }
      // }

      // if (brandingContent) {
      //   const currentChatConfig = store.botData?.chatConfig || {};
      //   const nextBotData = {
      //     ...store.botData,
      //     chatConfig: await buildChatConfigFromBranding(
      //       currentChatConfig,
      //       brandingContent,
      //     ),
      //   };

      //   if (onDataChange) {
      //     onDataChange("root", nextBotData);
      //   } else {
      //     storeDispatch({ type: "set-botData", botData: nextBotData });
      //   }
      // }

      if (!onTrainComplete) {
        onClose();
        setIsInputVisible(false);
      }
      setTimeout(() => {
        sendInstructionMsg();
      }, 10 * 1000);
    } catch (err) {
      console.error(err);
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Unable to train data.",
        }),
      );
    } finally {
      setIsTraining(false);
    }
  }

  const pagination = Array.from(
    { length: Math.ceil(crawlData.length / URL_PER_PAGE) },
    (_, index) => index,
  );
  const paginationWindow = Math.floor(page / PAGE_PER_WINDOW);

  function toggleChecked(id: string) {
    if (checkedSet.has(id)) {
      checkedSet.delete(id);
    } else {
      checkedSet.add(id);
    }

    setCheckedSet(new Set(checkedSet));
  }

  function unselectAll() {
    storeDispatch({ type: "crawl-set-selected-ids", ids: [] });
    storeDispatch({ type: "extra-delete-all" });
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message:
          "Selected links have been successfully removed from training data.",
      }),
    );
  }

  function deleteLink(id: string) {
    storeDispatch({ type: "crawl-delete", id });

    dispatch(
      userSlice.actions.setToastPopUp({
        toastPopUp: {
          show: true,
          toastType: "success",
          message: "The link has been successfully deleted.",
        },
      }),
    );
  }

  function toggleSelection(id: string) {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  }

  function deleteMultiple(selectedIds: string[]) {
    selectedIds.forEach((id) => {
      deleteLink(id);
    });

    setSelectedIds([]);
  }

  function toggleLink(id: string) {
    const data = crawlData.find((item) => item.id === id);
    if (!data || existingUrlKeys.has(getUrlKey(data.url))) return false;

    // if (
    //   !data.selected &&
    //   selectedCharacterCount + data.characterCount > characterRemaining
    // ) {
    //   dispatch(
    //     userSlice.actions.setToastPopUp({
    //       show: true,
    //       toastType: "error",
    //       message:
    //         "Max character limit reached. Remove other links or upgrade your plan.",
    //     }),
    //   );
    //   return;
    // }

    // if (data.selected) {
    //   dispatch(
    //     userSlice.actions.setToastPopUp({
    //       show: true,
    //       toastType: "success",
    //       message:
    //         "Selected links have been successfully removed from training data.",
    //     }),
    //   );
    // }

    storeDispatch({ type: "crawl-toggle", id });
    return true;
  }

  function toggleAllLink() {
    if (allChecked) {
      storeDispatch({ type: "crawl-set-selected-ids", ids: [] });
      setSelectedIds([]);
    } else {
      let characterCount = 0;
      let counter = 0;
      const newSelectedIds: string[] = [];
      const seenUrlKeys = new Set(existingUrlKeys);

      for (const [key, value] of store.crawlData) {
        const urlKey = getUrlKey(value.url);
        if (!urlKey || seenUrlKeys.has(urlKey)) continue;
        seenUrlKeys.add(urlKey);

        if (characterCount + value.characterCount > characterRemaining) {
          dispatch(
            userSlice.actions.setToastPopUp({
              show: true,
              toastType: "error",
              message: `Maximum character limit reached. Only ${counter} links within the plan limit will be selected.`,
            }),
          );
          break;
        }
        characterCount += value.characterCount;
        counter++;
        newSelectedIds.push(key);
      }
      storeDispatch({
        type: "crawl-set-selected-ids",
        ids: newSelectedIds,
      });

      setSelectedIds(newSelectedIds);
    }
  }

  function handleSearchInput(event: React.ChangeEvent<HTMLInputElement>) {
    setSearchInput(event.target.value);
    setPage(0);
  }

  function handlePagination(direction: "next" | "back") {
    if (
      direction === "next" &&
      (paginationWindow + 1) * PAGE_PER_WINDOW < pagination.length - 1
    ) {
      setPage((paginationWindow + 1) * PAGE_PER_WINDOW);
    }
    if (direction === "back" && (paginationWindow - 1) * PAGE_PER_WINDOW > -1) {
      setPage((paginationWindow - 1) * PAGE_PER_WINDOW);
    }
  }

  return (
    <div
      className={`createbot-modal data-source-modal ${fromOnboarding ? "onboarding-training-modal" : ""} ${fromOutgrow ? "outgrow" : ""}`}
    >
      <div
        className="linkPopup-container link-modal-wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={`close flex-cnt ${isAccordian === 1 ? "close-top" : ""}`}
          onClick={onClose}
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="#6D6D6D"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 1.00714L8.99286 0L5 3.99286L1.00714 0L0 1.00714L3.99286 5L0 8.99286L1.00714 10L5 6.00714L8.99286 10L10 8.99286L6.00714 5L10 1.00714Z"
              fill="#6D6D6D"
            />
          </svg>
        </div>
        <div className="linkPopup-header fx p-relative jstfy-sp-btw w-link-header">
          <div className="allFetchedUrlHead fx al-fx-cnt gap-20">
            <label className="checkbox-container link-mark w-inc">
              <input
                className="bot-check none"
                type="checkbox"
                checked={allChecked}
                onChange={toggleAllLink}
              />
              <span className="mark">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <rect width="16" height="16" rx="5" fill="#FB5F66" />
                  <path
                    d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                    fill="white"
                  />
                </svg>
              </span>
            </label>
            <div className="fetchlinks-header-headings fx fx-d-col gap-6">
              <h5 className="font-medium">
                {fromOnboarding
                  ? "Selected links for training"
                  : "Fetched Links"}
              </h5>
              <h6>
                {fromOnboarding ? (
                  <>
                    Total Fetched Links:{" "}
                    <span className="font-medium">{crawlLength}</span>
                  </>
                ) : (
                  ` (Total Fetched Links: ${crawlLength})`
                )}
              </h6>
            </div>
          </div>
          <div className="fx al-fx-cnt fetchLinksActionsRight gap-20">
            <div
              ref={boxRef}
              className="fetchUrlSearchBox fx al-fx-cnt cp gap-10"
              onClick={() => setIsInputVisible(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
              >
                <g clipPath="url(#clip0_1952_771)">
                  <path
                    d="M5.56621 0.5C6.94534 0.5 8.26798 1.04786 9.24317 2.02304C10.2184 2.99823 10.7662 4.32087 10.7662 5.7C10.7662 6.988 10.2942 8.172 9.51821 9.084L9.73421 9.3H10.3662L14.3662 13.3L13.1662 14.5L9.16621 10.5V9.868L8.95021 9.652C8.00665 10.4574 6.80678 10.8999 5.56621 10.9C4.18709 10.9 2.86444 10.3521 1.88926 9.37696C0.914066 8.40177 0.366211 7.07913 0.366211 5.7C0.366211 4.32087 0.914066 2.99823 1.88926 2.02304C2.86444 1.04786 4.18709 0.5 5.56621 0.5ZM5.56621 2.1C3.56621 2.1 1.96621 3.7 1.96621 5.7C1.96621 7.7 3.56621 9.3 5.56621 9.3C7.56621 9.3 9.16621 7.7 9.16621 5.7C9.16621 3.7 7.56621 2.1 5.56621 2.1Z"
                    fill="#444444"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1952_771">
                    <rect
                      width="14"
                      height="14"
                      fill="white"
                      transform="translate(0.366211 0.5)"
                    />
                  </clipPath>
                </defs>
              </svg>
              <AnimatePresence>
                {isInputVisible && (
                  <motion.input
                    key="animated-input"
                    type="text"
                    placeholder="Search Links"
                    autoFocus
                    value={searchInput}
                    onChange={handleSearchInput}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "200px", opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    style={{
                      overflow: "hidden",
                      padding: "6px 10px",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                    }}
                  />
                )}
              </AnimatePresence>
              {/* {isInputVisible && (
                <input
                  type="text"
                  placeholder="Search Links"
                  autoFocus
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              )} */}
            </div>

            {selectedData.length ? (
              <button
                className={`deleteFetchedUrlBtn fx gap-8 align-middle ${selectedData.length && "enabled"}`}
                onClick={() => deleteMultiple(selectedIds)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="17"
                  viewBox="0 0 16 17"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.4382 3.5C15.4382 2.94772 14.9905 2.5 14.4382 2.5H11.9208C11.4996 1.30491 10.372 0.504062 9.10483 0.5H7.10483C5.83767 0.504062 4.71008 1.30491 4.28883 2.5H1.77148C1.2192 2.5 0.771484 2.94772 0.771484 3.5C0.771484 4.05228 1.2192 4.5 1.77148 4.5H2.10483V12.8333C2.10483 14.8584 3.74645 16.5 5.77148 16.5H10.4381C12.4632 16.5 14.1048 14.8584 14.1048 12.8333V4.5H14.4382C14.9905 4.5 15.4382 4.05228 15.4382 3.5ZM12.1048 12.8333C12.1048 13.7538 11.3586 14.5 10.4382 14.5H5.77148C4.85102 14.5 4.10483 13.7538 4.10483 12.8333V4.5H12.1048V12.8333ZM6.43774 12.499C6.99003 12.499 7.43774 12.0513 7.43774 11.499V7.49902C7.43774 6.94674 6.99003 6.49902 6.43774 6.49902C5.88546 6.49902 5.43774 6.94674 5.43774 7.49902V11.499C5.43774 12.0513 5.88546 12.499 6.43774 12.499ZM10.7722 11.499C10.7722 12.0513 10.3245 12.499 9.77222 12.499C9.21994 12.499 8.77222 12.0513 8.77222 11.499V7.49902C8.77222 6.94674 9.21994 6.49902 9.77222 6.49902C10.3245 6.49902 10.7722 6.94674 10.7722 7.49902V11.499Z"
                    fill="#666666"
                  />
                </svg>
                <span className="flex-cnt">
                  {selectedIds.length > 1 ? "Delete All" : "Delete"}
                </span>
              </button>
            ) : null}
          </div>
        </div>
        <div className="linkPopup-body fx fx-d-col gap-10">
          <div className="linkPopupAddUrl fx jstfy-sp-btw pd-15">
            <div className="fx al-fx-cnt w-100 gap-20">
              <LinkIcon />
              <input
                type="text"
                className="link_input"
                placeholder="Enter link to add"
                value={extraUrl}
                onChange={(event) => setExtraUrl(event.target.value)}
              />
            </div>
            <button
              className="add-links"
              onClick={() => fetchExtraUrl(selectedCharacterCount, false)}
            >
              {extraLoader ? (
                <div className="fx al-fx-end jstfy-cnt fw-500 gap-6">
                  Adding
                  <ThreeDot />
                </div>
              ) : (
                "Add"
              )}
            </button>
          </div>
          {fromOnboarding && (
            <div className="flex h-[47px] items-center gap-[10px] rounded-[10px] border border-[rgba(0,0,0,0.1)] bg-[#f9f9f9] p-[15px]">
              <SearchLinkIcon />
              <input
                onChange={handleSearchInput}
                type="text"
                placeholder="Search link..."
                className="!border-none bg-transparent !p-0 outline-none"
              />
            </div>
          )}

          {crawlData.length ? (
            <table className="links-table table-links fetched-links-table">
              <div
                className={`table-scroll ${isAccordian === 1 ? "none" : ""}`}
              >
                <AnimatePresence>
                  {fromOnboarding && crawlData.length > 0 && (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, transition: { duration: 0.3 } }}
                      layout
                      className="search-links-td links-table-data links_data cursor-pointer hover:bg-[#F9F9F9]"
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (
                          target.closest(".checkbox-container") ||
                          target.closest("svg")
                        ) {
                          return;
                        }
                        toggleAllLink();
                      }}
                    >
                      <td className="ai-self-start flex items-center gap-[16px]">
                        <label className="checkbox-container link-mark">
                          <input
                            className="bot-check"
                            type="checkbox"
                            checked={allChecked}
                            onChange={toggleAllLink}
                            onClick={(e) => e.stopPropagation()}
                          />

                          <span className="mark">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <rect
                                width="16"
                                height="16"
                                rx="5"
                                fill="#FB5F66"
                              />
                              <path
                                d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                                fill="white"
                              />
                            </svg>
                          </span>
                        </label>
                        <p className="mt-[7px] text-[14px] font-medium leading-[110%]">
                          Select All
                        </p>
                      </td>
                    </motion.tr>
                  )}
                  {paginatedCrawlData.map((data) => (
                    <motion.tr
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, transition: { duration: 0.3 } }}
                      key={data.id}
                      layout
                      className="links-table-data links_data cursor-pointer hover:bg-[#F9F9F9]"
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (
                          target.closest(".checkbox-container") ||
                          target.closest("svg")
                        ) {
                          return;
                        }
                        if (toggleLink(data.id)) {
                          toggleSelection(data.id);
                        }
                      }}
                    >
                      <td className="ai-self-start">
                        <label className="checkbox-container link-mark">
                          <input
                            className="bot-check"
                            type="checkbox"
                            checked={data.selected}
                            disabled={existingUrlKeys.has(getUrlKey(data.url))}
                            onChange={() => {
                              if (toggleLink(data.id)) {
                                toggleSelection(data.id);
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />

                          <span className="mark">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <rect
                                width="16"
                                height="16"
                                rx="5"
                                fill="#FB5F66"
                              />
                              <path
                                d="M11.6923 4.11088C11.3794 3.90411 10.9616 3.99489 10.7589 4.31353L6.9773 10.2532L5.13304 8.51563C4.85926 8.25774 4.43251 8.2747 4.17942 8.55368C3.92634 8.83243 3.94298 9.26775 4.21677 9.52541L6.63828 11.8068C6.63828 11.8068 6.70779 11.8677 6.73996 11.8891C6.85334 11.9642 6.98067 12 7.10665 12C7.32757 12 7.54421 11.8895 7.67356 11.6864L11.8912 5.06198C12.0941 4.74334 12.005 4.31742 11.6923 4.11088Z"
                                fill="white"
                              />
                            </svg>
                          </span>
                        </label>
                      </td>
                      {/* <td><input type="text" value={data.link} className="crawled-links"/></td> */}
                      <td className="link-container-td">
                        <div
                          className={`link-container ${
                            data.url.length > 90 && "onHover"
                          }`}
                        >
                          <p className="link">{data.url}</p>
                        </div>
                      </td>
                      <td className="delete-link-td">
                        <svg
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteLink(data.id);
                          }}
                          width="32"
                          height="33"
                          viewBox="0 0 32 33"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="0.712891"
                            y="0.5"
                            width="30.7265"
                            height="32"
                            rx="8"
                            fill="#FFEDEE"
                          />
                          <path
                            d="M21.8031 12.4088C21.5862 12.4088 21.3781 12.495 21.2246 12.6485C21.0712 12.8019 20.985 13.01 20.985 13.227V22.3828C20.9615 22.7965 20.7755 23.1842 20.4675 23.4614C20.1595 23.7386 19.7544 23.8829 19.3405 23.8628H12.8118C12.3979 23.8829 11.9928 23.7386 11.6848 23.4614C11.3768 23.1842 11.1908 22.7965 11.1673 22.3828V13.227C11.1673 13.01 11.0811 12.8019 10.9277 12.6485C10.7743 12.495 10.5662 12.4088 10.3492 12.4088C10.1322 12.4088 9.92409 12.495 9.77066 12.6485C9.61723 12.8019 9.53103 13.01 9.53103 13.227V22.3828C9.55438 23.2306 9.91276 24.0345 10.5277 24.6186C11.1426 25.2027 11.9639 25.5193 12.8118 25.4991H19.3405C20.1884 25.5193 21.0097 25.2027 21.6246 24.6186C22.2395 24.0345 22.5979 23.2306 22.6213 22.3828V13.227C22.6213 13.01 22.5351 12.8019 22.3816 12.6485C22.2282 12.495 22.0201 12.4088 21.8031 12.4088ZM22.6213 9.95442H19.3487V8.31814C19.3487 8.10116 19.2625 7.89306 19.1091 7.73963C18.9557 7.5862 18.7476 7.5 18.5306 7.5H13.6217C13.4047 7.5 13.1967 7.5862 13.0432 7.73963C12.8898 7.89306 12.8036 8.10116 12.8036 8.31814V9.95442H9.53103C9.31405 9.95442 9.10595 10.0406 8.95252 10.194C8.79909 10.3475 8.71289 10.5556 8.71289 10.7726C8.71289 10.9895 8.79909 11.1976 8.95252 11.3511C9.10595 11.5045 9.31405 11.5907 9.53103 11.5907H22.6213C22.8383 11.5907 23.0464 11.5045 23.1998 11.3511C23.3532 11.1976 23.4394 10.9895 23.4394 10.7726C23.4394 10.5556 23.3532 10.3475 23.1998 10.194C23.0464 10.0406 22.8383 9.95442 22.6213 9.95442ZM14.4399 9.95442V9.13628H17.7124V9.95442H14.4399Z"
                            fill="#FB5F66"
                          />
                          <path
                            opacity="0.5"
                            d="M15.2574 20.59V14.8631C15.2574 14.6461 15.1712 14.438 15.0177 14.2845C14.8643 14.1311 14.6562 14.0449 14.4392 14.0449C14.2222 14.0449 14.0142 14.1311 13.8607 14.2845C13.7073 14.438 13.6211 14.6461 13.6211 14.8631V20.59C13.6211 20.807 13.7073 21.0151 13.8607 21.1686C14.0142 21.322 14.2222 21.4082 14.4392 21.4082C14.6562 21.4082 14.8643 21.322 15.0177 21.1686C15.1712 21.0151 15.2574 20.807 15.2574 20.59ZM18.5299 20.59V14.8631C18.5299 14.6461 18.4437 14.438 18.2903 14.2845C18.1369 14.1311 17.9288 14.0449 17.7118 14.0449C17.4948 14.0449 17.2867 14.1311 17.1333 14.2845C16.9799 14.438 16.8937 14.6461 16.8937 14.8631V20.59C16.8937 20.807 16.9799 21.0151 17.1333 21.1686C17.2867 21.322 17.4948 21.4082 17.7118 21.4082C17.9288 21.4082 18.1369 21.322 18.2903 21.1686C18.4437 21.0151 18.5299 20.807 18.5299 20.59Z"
                            fill="#FB5F66"
                          />
                        </svg>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </div>
              <tfoot
                className={`flex items-center justify-center border-t border-solid border-[#D9D9D9] py-[16px]`}
              >
                <Pagination
                  currentPage={page + 1}
                  totalPages={pagination.length}
                  totalItems={crawlData.length}
                  itemsPerPage={URL_PER_PAGE}
                  onPageChange={(newPage) => setPage(newPage - 1)}
                  showFirstLast={true}
                  showItemsInfo={true}
                />
                <span
                  className={`fetchMoreLinksCTA p-absolute fx fx-al-cnt cp ${fetchRemains ? "fx cp gap-10" : "fx cp btn-disable gap-10"}`}
                  onClick={() => fetchRemains && fetchMoreUrl()}
                >
                  {fetchMoreLoader ? <CircleLoader /> : <PrimaryClrLinkIcon />}
                  Fetch more links
                </span>
              </tfoot>
            </table>
          ) : (
            <div className="no-links-found">
              <img
                src="https://dlvkyia8i4zmz.cloudfront.net/XkSd2WcLQuyhxKMBQ9Jg_Group_14507.svg"
                alt=""
              />
              <span className="fx al-cnt">
                {allTrained ? "All links already trained" : "No links found"}
              </span>
            </div>
          )}
        </div>

        <div className="linkPopup-footer wd-100 flex-cnt gap-20">
          <button className="cancelFetchUrlLinks" onClick={onClose}>
            cancel
          </button>
          <button
            className={`trainFetchUrlLinks ${selectedLength ? "" : "fetch-disable"} `}
            onClick={handleTrain}
            disabled={selectedLength === 0 || isTraining}
          >
            {isTraining ? (
              <div className="fx al-fx-end jstfy-cnt fw-500 gap-6">
                Training
                <ThreeDot />
              </div>
            ) : fromOnboarding ? (
              `Train  Selected Links ${selectedLength ? `(${selectedLength})` : ""}`
            ) : (
              "Train"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FetchUrlModal;

const MagnifyIcon = () => (
  <svg
    width="14"
    height="15"
    viewBox="0 0 14 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_256_3554)">
      <path
        d="M5.2 0.5C6.57913 0.5 7.90177 1.04786 8.87696 2.02304C9.85215 2.99823 10.4 4.32087 10.4 5.7C10.4 6.988 9.928 8.172 9.152 9.084L9.368 9.3H10L14 13.3L12.8 14.5L8.8 10.5V9.868L8.584 9.652C7.64044 10.4574 6.44057 10.8999 5.2 10.9C3.82087 10.9 2.49823 10.3521 1.52304 9.37696C0.547855 8.40177 0 7.07913 0 5.7C0 4.32087 0.547855 2.99823 1.52304 2.02304C2.49823 1.04786 3.82087 0.5 5.2 0.5ZM5.2 2.1C3.2 2.1 1.6 3.7 1.6 5.7C1.6 7.7 3.2 9.3 5.2 9.3C7.2 9.3 8.8 7.7 8.8 5.7C8.8 3.7 7.2 2.1 5.2 2.1Z"
        fill="black"
        fillOpacity="0.5"
      />
    </g>
    <defs>
      <clipPath id="clip0_256_3554">
        <rect
          width="14"
          height="14"
          fill="white"
          transform="translate(0 0.5)"
        />
      </clipPath>
    </defs>
  </svg>
);
